import { useEffect, useRef } from 'react';
import { match } from '@blind-kings/game-core';
import type { SnackBarOptions } from '../types/app';

export type SnackBarProps = SnackBarOptions & {
  id?: string;
  onClose?: () => void;
};

export default function SnackBar({
  variant,
  title,
  message,
  timeout,
  onClose = () => {},
}: SnackBarProps) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (timeout && onClose) {
      timeoutRef.current = setTimeout(() => {
        onClose();
      }, timeout);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [timeout, onClose]);

  const borderClass = match(variant)
    .against({
      danger: 'border-bk-danger',
      warning: 'border-bk-gold',
      info: 'border-bk-accent',
      success: 'border-emerald-500',
    })
    .otherwise('border-bk-border') as string;

  const titleClass = match(variant)
    .against({
      danger: 'text-bk-danger-bright',
      warning: 'text-bk-gold-bright',
      info: 'text-sky-300',
      success: 'text-emerald-400',
    })
    .otherwise('text-bk-text') as string;

  return (
    <div
      className={`relative w-full min-w-fit border border-l-4 bg-bk-panel p-4 text-bk-text shadow-[0_8px_32px_rgba(0,0,0,0.55)] ${borderClass}`}
    >
      <div className={`font-bold uppercase tracking-wide ${titleClass}`}>{title}</div>
      <div className="text-sm text-bk-muted">{message}</div>
      <button
        type="button"
        className="absolute top-1 right-2 text-xs text-bk-muted hover:text-bk-danger"
        onClick={onClose}
      >
        ✕
      </button>
    </div>
  );
}
